import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { CartAdditionalInformationBlockConfig } from '@front-platform/content-builder';
import { cartAdditionalInformationBlockResolveConfig } from '@front-platform/content-builder';
import { map } from 'rxjs/operators';
import { Component, OnDestroy, input } from '@angular/core';
import { combineLatest } from 'rxjs';

import { LocationUtils } from '@shared/utils/location/location.utils';

import { ModalsService } from '@core/services/modals/modals.service';

import { CartCommentTypeValues } from '@config/values/cart.values';
import { OrderTypeValues } from 'src/app/config/values/order.values';

import { CartStoreSelector } from '@common/selectors/cart.selector';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';

import { CartCommentModalComponent } from '@shared/modals/cart-comment-modal/cart-comment-modal.component';
import { CartParametersModalComponent } from '@shared/modals/cart-parameters-modal/cart-parameters-modal.component';
import { CartUserInformationsModalComponent } from '@shared/modals/cart-user-informations-modal/cart-user-informations-modal.component';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
export interface CartAdditionalInformationBlockRuntimeConfig {}

export type CartAdditionalInformationBlockConfigInput = Partial<
  Omit<CartAdditionalInformationBlockConfig, keyof CartAdditionalInformationBlockRuntimeConfig> &
    CartAdditionalInformationBlockRuntimeConfig
>;
export type CartAdditionalInformationBlockResolvedConfig = Omit<
  CartAdditionalInformationBlockConfig,
  keyof CartAdditionalInformationBlockRuntimeConfig
> &
  Partial<CartAdditionalInformationBlockRuntimeConfig>;
export interface CartAdditionalInformationBlockContract extends ConfigComponentContract<
  CartAdditionalInformationBlockConfigInput,
  CartAdditionalInformationBlockResolvedConfig
> {}

@Component({
  selector: 'app-cart-additional-information-block',
  templateUrl: './cart-additional-information-block.component.html',
  standalone: false,
})
export class CartAdditionalInformationBlockComponent
  implements OnDestroy, CartAdditionalInformationBlockContract
{
  readonly config = input<CartAdditionalInformationBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    CartAdditionalInformationBlockConfig,
    CartAdditionalInformationBlockRuntimeConfig
  >(cartAdditionalInformationBlockResolveConfig, () => ({ config: this.config() }));
  get headingColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['heading_color'] as unknown as IContentBuilderFieldColor;
  }
  get textColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor;
  }

  message$ = this.cartSelector.selectActive$.pipe(map(cart => cart?.message));
  selectedDistribution$ = this.cartSelector.selectActive$.pipe(map(cart => cart?.type));
  // Prefer cart override; fall back to validated order phone (same source as the edit modal).
  contactPhone$ = combineLatest([
    this.cartSelector.selectActive$,
    this.orderSelector.selectValidation$,
  ]).pipe(
    map(
      ([cart, validation]) => cart?.contact_phone || validation?.contact_phone_number || undefined,
    ),
  );
  missingPhone$ = this.orderSelector.selectErrors.pipe(map(errors => errors.missing_number));
  instructions$ = this.cartSelector.selectActive$.pipe(
    map(cart => cart?.delivery_address?.instructions),
  );
  deliveryAddress$ = this.cartSelector.selectActive$.pipe(
    map(cart => {
      const deliveryAddress = cart?.delivery_address;
      if (deliveryAddress) {
        return LocationUtils.constructDisplayedDeliveryAddressWithValidateResponse(deliveryAddress);
      }
      return undefined;
    }),
  );

  orderTypeValues = OrderTypeValues;

  constructor(
    private cartSelector: CartStoreSelector,
    private orderSelector: OrderStoreSelector,
    private cartDispatcher: CartStoreDispatcher,
    private readonly modalsService: ModalsService,
  ) {}

  openInputComment(type: string): void {
    this.cartDispatcher.updateStore({
      comment: { type },
    });
    this.modalsService.open(CartCommentModalComponent.handle);
  }

  onRemoveComment(type: string): void {
    if (type === CartCommentTypeValues.Merchant) {
      this.cartDispatcher.updateActive({ message: '' });
    }
  }

  openInputUserInformation(): void {
    this.modalsService.open(CartUserInformationsModalComponent.handle);
  }

  openCartParameters(): void {
    this.modalsService.open(CartParametersModalComponent.handle);
  }

  ngOnDestroy(): void {
    this.modalsService.close(CartCommentModalComponent.handle);
    this.modalsService.close(CartUserInformationsModalComponent.handle);
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(CartAdditionalInformationBlockComponent, {
  'keyAliases': {},
});
